import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  Spin,
  Form,
  Tooltip,
  Typography,
  Table,
  Breadcrumb,
  Result,
} from "antd";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import dayjs from "dayjs";
import { fetchCompanies } from "../../redux/actions/companyActions";
import { fetchDayBreakDownReport } from "../../redux/actions/breakdownActions";
import { CalendarOutlined, BorderlessTableOutlined } from "@ant-design/icons";
import { getCurrentDateRange } from "../../utilities/report";
import HeaderSelector from "../../components/breakdownSetting/reportHeaderSelector";
import requireAuth from "../../hocs/requireAuth";
import { breakdownReport } from "../../constants/tableColumns/breakdownReport";
import { useExcelExport } from "../../hooks/useExcelExport";
import ExcelExportButton from "../../components/breakdownSetting/ExcelExportButton";
import { BREAKDOWN_PERMISSION, COMPANY_ADMIN_ROLE_ACCESS } from "../../constants";

const { Title } = Typography;

const PageBreadcrumb = () => {
  return (
    <Breadcrumb
      style={{ fontSize: "14px" }}
      items={[
        {
          title: <BorderlessTableOutlined style={{ fontSize: "16px" }} />,
        },
        {
          title: (
            <>
              <CalendarOutlined style={{ fontSize: "16px" }} />
              <span>Breakdown</span>
            </>
          ),
        },
        {
          title: "Breakdown Report",
        },
      ]}
    />
  );
};

const initialState = {
  company: undefined,
  templateType: undefined,
  template: undefined,
  template_name: undefined,
  interval: "week",
  date: getCurrentDateRange("week", dayjs()),
};

const BreakdownReportScreen = () => {
  const [loading, setLoading] = useState(false);
  const [showEmpty, setShowEmpty] = useState(false);
  const [breakdowns, setBreakdowns] = useState([]);
  const dispatch = useDispatch();
  const [config, setConfig] = useState(null);
  const [form] = Form.useForm();
  const [filter, setFilters] = useState(initialState);
  const { wrapperRef, onDownload, isReady } = useExcelExport(
    [breakdowns, breakdownReport],
    `Breakdown-Report-${get(filter, "date.[0]", "")}-to-${get(
      filter,
      "date.[1]",
      ""
    )}`,
    `Breakdown-Report`
  );
  const setinitialCompany = (company) => {
      const hasPermission = find(
             get(company, "feature_permissions", []),
             ['key', BREAKDOWN_PERMISSION]
           );
    
      if (!hasPermission) return;
    setFilters({
      ...filter,
      templateType: undefined,
      company,
    });
    setConfig({
      ...config,
      companies: [company],
      template_types: get(company, "template_access_list"),
      templates:undefined
    });
    form.setFieldsValue({
      template:undefined,
      template_type: undefined,
      Company: get(company, "id"),
    });
  };

  const getCompanies = () => {
    setLoading(true);
    dispatch(fetchCompanies())
      .then((result) => {
        if (get(result, "length") === 1) {
          setinitialCompany(result[0]);
        } else {
          setConfig({ companies: result });
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const getBreakDowns = () => {
    setLoading(true);
    const query = {
      from_date: get(filter, "date[0]"),
      to_date: get(filter, "date[1]"),
    };
    const uuid = get(filter, "template");
    dispatch(fetchDayBreakDownReport(uuid, query))
      .then((result) => {
        if (!isEmpty(result)) {
          setShowEmpty(false);
          setBreakdowns(result);
        } else {
          setBreakdowns([]);
          setShowEmpty(true);
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getCompanies();
  }, []);
  return (
    <div className="p-2 h-full flex flex-col">
      <div className="flex flex-row justify-between pb-4">
        <PageBreadcrumb />
      </div>
      <div className="h-full rounded-xl overflow-hidden bg-white flex-1 overflow-y-auto  pb-5 min-w-full">
        <Form form={form}>
          <Spin spinning={loading}>
            <HeaderSelector
              form={form}
              filter={filter}
              config={config}
              setConfig={setConfig}
              setFilters={setFilters}
              getBreakDowns={getBreakDowns}
            />

            {get(breakdowns, "length") > 0 && (
              <div className="w-full px-4 md:px-8">
                <Tooltip title="Download Excel" className="float-right my-4">
                  <ExcelExportButton
                    type="primary"
                    onDownload={onDownload}
                    disabled={!isReady}
                  />
                </Tooltip>
                <div className="w-full table-container">
                  <Title className="mt-10" level={5}>
                    {get(filter, "template_name")}
                  </Title>
                  <div className="w-full table-container" ref={wrapperRef}>
                    <Table
                      dataSource={breakdowns}
                      columns={breakdownReport(filter)}
                      size="small"
                    rowKey={(record) => get(record, "id") || crypto.randomUUID()}
                    />
                  </div>
                </div>
              </div>
            )}
            {showEmpty && (
              <div className="px-8 py-8">
                <Result
                  title={`No Breakdown exists...!`}
                  subTitle="Please try again with another company or template type."
                  status="404"
                />
              </div>
            )}
          </Spin>
        </Form>
      </div>
    </div>
  );
};
export default requireAuth(BreakdownReportScreen,COMPANY_ADMIN_ROLE_ACCESS,BREAKDOWN_PERMISSION);